import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, ConfirmationCode, Input, ModalBody, ModalFooter } from "@/modules/common/components";
import useResendCooldown from "@/modules/common/hooks/useResendCooldown";
import { FirstAccessFormData, firstAccessSchema, forgotPasswordEmailSchema } from "../../schemas";
import EmailModalStep from "./EmailModalStep";
import useVerifyFirstAccessCode from "../../hooks/useVerifyFirstAccessCode";

const RESEND_COOLDOWN_SECONDS = 30;

interface EmailCodeStepProps {
  onVerified: (email: string, code: string) => void;
}

const EmailCodeStep = ({ onVerified }: EmailCodeStepProps) => {
  const [codeModal, setCodeModal] = useState(false);
  const { cooldown: resendCooldown, start: startResendCooldown } = useResendCooldown(RESEND_COOLDOWN_SECONDS);
  const verifyFirstAccessCode = useVerifyFirstAccessCode();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FirstAccessFormData>({ resolver: zodResolver(firstAccessSchema), defaultValues: { email: "", code: "" } });

  const emailValue = watch("email");
  const isEmailValid = forgotPasswordEmailSchema.shape.email.safeParse(emailValue).success;

  const onSubmit = async (values: FirstAccessFormData) => {
    await verifyFirstAccessCode.mutateAsync({ email: values.email, code: Number(values.code) });
    onVerified(values.email, values.code);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className="gap-3">
          <div>
            <Input label="E-mail" maskType="email" markAsRequired data-test="first-access-email" {...register("email")} />
            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
          </div>

          <div>
            <label className="text-sm">
              Código de verificação
              <span className="text-red-500"> *</span>
            </label>
            <Controller
              name="code"
              control={control}
              render={({ field }) => <ConfirmationCode value={field.value} onChange={field.onChange} disabled={!isEmailValid} />}
            />
            {errors.code && <p className="mt-1 text-xs text-red-500">{errors.code.message}</p>}
          </div>
        </ModalBody>
        <ModalFooter className="flex flex-col">
          <Button
            type="submit"
            className="w-full"
            label="Verificar código"
            disabled={verifyFirstAccessCode.isPending}
            loading={verifyFirstAccessCode.isPending}
            data-test="first-access-verify-code"
          />

          <button
            type="button"
            className="w-fit text-sm text-ui-text-muted underline enabled:cursor-pointer enabled:hover:text-ui-text disabled:cursor-not-allowed disabled:opacity-60"
            disabled={resendCooldown > 0}
            onClick={() => setCodeModal(true)}
          >
            {resendCooldown > 0 ? `Não recebeu o código? Peça novamente em ${resendCooldown}s` : "Não recebeu o código? Peça novamente"}
          </button>
        </ModalFooter>
      </form>
      <EmailModalStep
        open={codeModal}
        onClose={() => setCodeModal(false)}
        onSent={(email) => {
          setValue("email", email);
          startResendCooldown();
        }}
      />
    </>
  );
};

export default EmailCodeStep;
